import { PostCardBody } from "@/components/common/PostCardBody";
import { PostCardHeaderActionArea } from "@/components/common/PostCardHeaderActionArea";
import { MediaPreview } from "@/components/common/MediaPreview";
import { PostCardMoveChannelDialog } from "@/components/common/PostCardMoveChannelDialog";
import { ReportDialog } from "@/components/common/ReportDialog";
import { SendingBadge } from "@/components/common/SendingBadge";
import { UserProfilePopupWrapper } from "@/components/common/UserProfilePopupWrapper";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import "@/index.css";
import { useNavigate } from "@tanstack/react-router";
import { Loader2, Pin } from "lucide-react";
import type React from "react";
import { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { PostCardFooter } from "@/components/common/PostCardFooter";
import { CONTENT_HIGHLIGHT_CLASS } from "@/features/study-group-files/constants/highlight";
import { usePostCardActions } from "@/components/common/hooks/usePostCardActions";
import type { PostChannelMoveOption } from "@/features/study-group-stream/types/postChannelMove";
import { toast } from "@/hooks/useToast";
import type {
  EventApiResponse,
  ReactionBreakdown,
  Reaction,
  ReactionType,
} from "@/lib/humhub-api/types";
import { cn } from "@/lib/utils";
import { copyPostLinkToClipboard } from "@/lib/utils/postUtils";
import { useGlobalUserInfo } from "@/providers/AuthProvider";

export interface MediaFile {
  id: number;
  guid: string;
  mime_type: string;
  size: number | string;
  file_name: string;
  url: string;
}

export interface PostCardMeta {
  author: string;
  authorId?: number;
  timestamp: string;
  avatarUrl: string | null;
  content: string;
  studyGroupName?: string;
  files?: MediaFile[];
}

export interface PostCardStatus {
  isPending?: boolean;
  isPinned?: boolean;
  isPinning?: boolean;
  isReacting?: boolean;
  isFavoriting?: boolean;
  isFavorited?: boolean;
  isCurrentUserAuthor?: boolean;
  currentReaction?: ReactionType | null;
  reactionsTotal?: number;
  reactionsPreview?: Reaction[];
  reactionsBreakdown?: ReactionBreakdown;
  commentsCount?: number;
  isEditing?: boolean;
  isDeleting?: boolean;
  disableNavigation?: boolean;
  isGroupAdmin?: boolean;
  hideOptionsButton?: boolean;
  isCommentActive?: boolean;
  isMinimized?: boolean;
  isArchived?: boolean;
  isHighlighted?: boolean;
  isMovingChannel?: boolean;
  movableChannels?: PostChannelMoveOption[];
  hideMinimizeButton?: boolean;
}

export interface PostCardIds {
  postId: number;
  postContentId: number;
  studyGroupId?: number;
}

export interface PostCardHandlers {
  onReaction?: (reaction: ReactionType | null) => Promise<void>;
  onPin?: (isPinned: boolean) => Promise<void>;
  onReport?: (reason: number, details: string) => Promise<void>;
  onToggleFavorite?: (
    postContentId: number,
    isCurrentlyFavorited: boolean,
  ) => Promise<void>;
  onCommentToggle?: () => void;
  onEdit?: (postId: number, newMessage: string) => Promise<void>;
  onDelete?: (postId: number) => Promise<void>;
  onSendMessage?: (userId: number) => void;
  onEventClick?: (eventData: EventApiResponse) => void;
  onToggleMinimize?: () => void;
  onMoveChannel?: (channel: PostChannelMoveOption) => Promise<void>;
}

export interface PostCardViewModel {
  meta: PostCardMeta;
  status: PostCardStatus;
  ids: PostCardIds;
  handlers: PostCardHandlers;
}

export const PostCard: React.FC<PostCardViewModel> = ({
  meta,
  status,
  ids,
  handlers,
}) => {
  const {
    author,
    authorId,
    timestamp,
    avatarUrl,
    content,
    studyGroupName,
    files = [],
  } = meta;
  const {
    isPending = false,
    isPinned = false,
    isPinning = false,
    isReacting = false,
    isFavoriting = false,
    isFavorited,
    isCurrentUserAuthor = false,
    currentReaction = null,
    reactionsTotal = 0,
    reactionsPreview = [],
    reactionsBreakdown,
    commentsCount,
    isEditing = false,
    isDeleting = false,
    disableNavigation = false,
    isGroupAdmin = false,
    hideOptionsButton = false,
    isCommentActive = false,
    isMinimized = false,
    isArchived = false,
    isHighlighted = false,
    isMovingChannel = false,
    movableChannels = [],
    hideMinimizeButton = false,
  } = status;
  const { postId, postContentId, studyGroupId } = ids;
  const {
    onReaction,
    onPin,
    onReport,
    onToggleFavorite,
    onCommentToggle,
    onEdit,
    onDelete,
    onSendMessage,
    onEventClick,
    onToggleMinimize,
    onMoveChannel,
  } = handlers;
  const hasLoggedRef = useRef(false);
  if (!hasLoggedRef.current) {
    hasLoggedRef.current = true;
  }
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userInfo = useGlobalUserInfo();
  const canMoveChannel =
    Boolean(onMoveChannel) &&
    movableChannels.length > 0 &&
    (isCurrentUserAuthor || isGroupAdmin);
  const {
    localReaction,
    localPinned,
    showReportDialog,
    isSubmittingReport,
    isEditMode,
    editedMessage,
    setEditedMessage,
    isSubmittingEdit,
    isPostDeleting,
    isMoveDialogOpen,
    setIsMoveDialogOpen,
    selectedMoveChannelId,
    setSelectedMoveChannelId,
    deleteDialog,
    handleReactionSelect,
    handlePinPost,
    handleReportSubmit,
    setShowReportDialog,
    handleEditClick,
    handleCancelEdit,
    handleSubmitEdit,
    handleDeleteClick,
    handleDeleteConfirm,
    handleKeyDown,
    handleCommentClick,
    handleMoveChannelClick,
    handleSubmitMoveChannel,
  } = usePostCardActions({
    content,
    postId,
    currentReaction,
    isPinned,
    isEditing,
    isDeleting,
    onReaction,
    onPin,
    onReport,
    onEdit,
    onDelete,
    onCommentToggle,
    onMoveChannel,
    movableChannels,
    canMoveChannel,
    onReportSuccess: () => {
      toast({
        title: t("common.success"),
        description: t("common.reportSuccess", {
          defaultValue: "Report submitted",
        }),
      });
    },
    onReportError: () => {
      toast({
        title: t("common.error"),
        description: t("common.reportError", {
          defaultValue: "Failed to submit report. Please try again.",
        }),
        variant: "error",
      });
    },
    onEditValidationError: () => {
      toast({
        title: t("common.error"),
        description: t("common.emptyPostMessage", {
          defaultValue: "Post message cannot be empty",
        }),
        variant: "error",
      });
    },
    onEditError: () => {
      toast({
        title: t("common.error"),
        description: t("common.editPostError", {
          defaultValue: "Failed to save changes. Please try again.",
        }),
        variant: "error",
      });
    },
    onDeleteError: () => {
      toast({
        title: t("common.error"),
        description: t("common.deleteError"),
        variant: "error",
      });
    },
  });

  const isCompact = isMinimized && !isEditMode;
  const firstLine = useMemo(() => {
    const lines = content.split(/\r?\n/).map((line) => line.trim());
    return lines.find((line) => line.length > 0) || content;
  }, [content]);
  const popupConfig = useMemo(
    () => ({
      trigger: "click" as const,
      usePortal: true,
      position: "left" as const,
    }),
    [],
  );

  // ナビゲーション可能かどうかを判定
  const canNavigate =
    !disableNavigation && studyGroupId && !isEditMode && !isPending;

  // PostCard クリック時のナビゲーション処理
  const handlePostCardClick = (e: React.MouseEvent) => {
    if (!canNavigate || e.defaultPrevented) {
      return;
    }

    const target = e.target as HTMLElement;
    const clickableElements = [
      "button",
      "a",
      "input",
      "textarea",
      "select",
      '[role="button"]',
      "[data-no-navigate]",
      ".author-clickable",
    ];

    if (
      clickableElements.some(
        (selector) => target.closest(selector) || target.matches(selector),
      )
    ) {
      return;
    }

    navigate({
      to: "/study-group/$study-group-id/post/$post-id",
      params: {
        "study-group-id": String(studyGroupId),
        "post-id": String(postId),
      },
    });
  };

  const handleCopyLink = async () => {
    if (!studyGroupId) {
      return;
    }

    try {
      await copyPostLinkToClipboard(studyGroupId, postId);
      toast({
        title: t("common.success"),
        description: t("common.linkCopied"),
      });
    } catch (error) {
      console.error("Failed to copy post link:", error);
      toast({
        title: t("common.error"),
        description: t("common.linkCopyFailed"),
        variant: "error",
      });
    }
  };

  const isAnyActionLoading =
    isPinning || isSubmittingEdit || isPostDeleting || isMovingChannel;
  const postBackgroundClass = isHighlighted
    ? CONTENT_HIGHLIGHT_CLASS
    : "stream-bg-post";

  return (
    <>
      <ConfirmationDialog
        open={deleteDialog.isOpen}
        onOpenChange={(open) => {
          if (!open) deleteDialog.close();
        }}
        title={t("common.delete")}
        description={t("common.confirmDelete")}
        confirmText={t("common.delete")}
        cancelText={t("common.cancel")}
        onConfirm={handleDeleteConfirm}
        isLoading={isPostDeleting}
        variant="destructive"
      />
      <PostCardMoveChannelDialog
        open={isMoveDialogOpen}
        onOpenChange={setIsMoveDialogOpen}
        isMovingChannel={isMovingChannel}
        movableChannels={movableChannels}
        selectedMoveChannelId={selectedMoveChannelId}
        onSelectedMoveChannelIdChange={setSelectedMoveChannelId}
        onSubmit={handleSubmitMoveChannel}
      />
      <article
        className={cn(
          postBackgroundClass,
          "grid w-full grid-cols-[auto,1fr] gap-3 rounded-tr-2xl pb-3 pl-3 pr-6 pt-3",
          isCompact && "min-h-[56px]",
          isPending && "animate-pulse",
          localPinned && "border-l-4 border-blue-500",
          isAnyActionLoading && "opacity-70",
          canNavigate && "cursor-pointer",
        )}
        onClick={handlePostCardClick}
      >
        <div className="pt-1">
          <UserProfilePopupWrapper
            userId={authorId ?? 0}
            userName={author}
            avatarUrl={avatarUrl}
            onSendMessage={onSendMessage}
            currentUserId={userInfo?.id}
            config={popupConfig}
            className="size-12 cursor-pointer transition-opacity hover:opacity-80"
          >
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={`${author}'s avatar`}
                className="size-12 rounded"
              />
            ) : (
              <div className="flex size-12 items-center justify-center rounded bg-gray-200">
                {author[0]}
              </div>
            )}
          </UserProfilePopupWrapper>
        </div>

        <div className="flex min-w-0 flex-col gap-3">
          <header className="flex items-start justify-between gap-2">
            <div className="flex flex-col gap-1">
              {!isCompact && studyGroupName && (
                <span className="text-sm text-gray-600">{studyGroupName}</span>
              )}
              <div className="mt-2 flex flex-col">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-variable-collection-black-gray">
                    {author}
                  </h3>
                  {!isCompact && localPinned && (
                    <Pin size={14} className="text-blue-500" />
                  )}
                </div>
                <span className="text-xs font-medium text-rw-dark-gray">
                  {timestamp}
                </span>
                {isPending && <SendingBadge />}
              </div>
            </div>
            <PostCardHeaderActionArea
              isPending={isPending}
              isFavorited={isFavorited}
              isFavoriting={isFavoriting}
              postContentId={postContentId}
              onToggleFavorite={!isArchived ? onToggleFavorite : undefined}
              onCopyLink={studyGroupId ? handleCopyLink : undefined}
              isCurrentUserAuthor={isCurrentUserAuthor}
              isGroupAdmin={isGroupAdmin}
              isPinned={localPinned}
              isMenuDisabled={
                isPinning || isEditMode || isSubmittingEdit || isMovingChannel
              }
              isDeleteDisabled={isPostDeleting}
              hideOptionsButton={hideOptionsButton || isArchived}
              isMinimized={isMinimized}
              onToggleMinimize={
                hideMinimizeButton ? undefined : onToggleMinimize
              }
              onReportClick={() => setShowReportDialog(true)}
              onEditClick={handleEditClick}
              onDeleteClick={handleDeleteClick}
              onPinClick={handlePinPost}
              canMoveChannel={canMoveChannel}
              onMoveChannelClick={handleMoveChannelClick}
            />
          </header>

          <PostCardBody
            isCompact={isCompact}
            firstLine={firstLine}
            isEditMode={isEditMode}
            editedMessage={editedMessage}
            content={content}
            isSubmittingEdit={isSubmittingEdit}
            onEditedMessageChange={setEditedMessage}
            onKeyDown={handleKeyDown}
            onCancelEdit={handleCancelEdit}
            onSubmitEdit={handleSubmitEdit}
            onEventClick={onEventClick}
            studyGroupId={studyGroupId}
          />

          {!isCompact && files && files.length > 0 && (
            <div className="mt-2">
              <MediaPreview files={files} />
            </div>
          )}

          {!isCompact && commentsCount !== undefined && (
            <div className="mt-0 border-t border-gray-200 pt-0">
              <PostCardFooter
                isPending={isPending}
                isReacting={isReacting}
                localReaction={localReaction}
                onReactionSelect={handleReactionSelect}
                reactionsTotal={reactionsTotal}
                reactionsPreview={reactionsPreview}
                reactionsBreakdown={reactionsBreakdown}
                postContentId={postContentId}
                showCommentButton={commentsCount !== undefined}
                onCommentClick={handleCommentClick}
                isCommentActive={isCommentActive}
                disabled={isArchived}
              />
            </div>
          )}
          {!isCompact &&
            (isPending || isPinning || isSubmittingEdit || isPostDeleting) && (
              <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="size-3 animate-spin" />
                {t("common.loading")}
              </div>
            )}
        </div>
      </article>

      <ReportDialog
        isOpen={showReportDialog}
        onClose={() => setShowReportDialog(false)}
        onSubmit={handleReportSubmit}
        isSubmitting={isSubmittingReport} // 内部管理に変更
      />
    </>
  );
};
